import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button, Table, Message, Icon, Input, Segment, Header, Dropdown, Checkbox, Form, Label, Loader,
} from 'semantic-ui-react';
import apiClient from '../../apiTools/apiClient';
import ConfirmModal from '../../components/prototype/ConfirmModal';

/**
 * ReaderAccess — who may listen to what, and the invitation that tells them.
 *
 * ⭐ WHAT THIS REPLACES, in the operator's words: *"to send an access key to a person, I have to
 * create a new key, then copy that key and paste it into an email."* Three surfaces and a
 * clipboard — and the key's one durable plaintext copy ending up in a Sent folder, which is the
 * part that actually mattered, since keys are hashed at rest and shown once precisely so the
 * plaintext has nowhere to live.
 *
 * So this screen does the whole thing: tick what they may hear, and the app sends the email.
 *
 * ─── ⛔ THE CHECKLIST SUBMITS A DIFF, NOT A DESIRED STATE ────────────────────────────────────────
 *
 * Every tick is compared against what the reader already has, and only the CHANGES are sent. A
 * screen that posted "here is everything they should have" would revoke whatever it had not loaded
 * — a stale tab, a filter left on, a release someone else added — and silent revocation is the one
 * failure a reader notices and nobody else does.
 *
 * ⚠️ BUILT ON CREATOR-SCOPED ENDPOINTS THOUGH ONLY AN ADMIN CAN REACH IT TODAY. `/api/reader-access`
 * and `/api/invitations` are guarded by `auth` and scoped by traversal in the service, so when this
 * becomes a Book Creator feature only the route guard changes. Building it on the admin release list
 * — which returns every release in the system — would have meant rebuilding the screen.
 *
 * ⓘ Collections ("a whole LitGroup") are a STANDING grant: books added later appear on the reader's
 * shelf without another invitation. That is a promise the welcome email already makes.
 */

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');

/** A reader's label for the picker: name, or the email we know them by. */
const readerLabel = (r) => (r.name ? `${r.name} (${r.email || 'no email'})` : (r.email || r.userId));

/**
 * ⓘ One row per release, flattened server-side; the grouping belongs here because only this screen
 * cares about it.
 */
function groupReleases(rows) {
  const groups = new Map();
  (rows || []).forEach((r) => {
    if (!groups.has(r.litGroupUuid)) {
      groups.set(r.litGroupUuid, { uuid: r.litGroupUuid, name: r.litGroupName, books: new Map() });
    }
    const g = groups.get(r.litGroupUuid);
    if (!g.books.has(r.bookUuid)) {
      g.books.set(r.bookUuid, { uuid: r.bookUuid, title: r.bookTitle, releases: [] });
    }
    g.books.get(r.bookUuid).releases.push(r);
  });
  return [...groups.values()].map((g) => ({ ...g, books: [...g.books.values()] }));
}

export default function ReaderAccess() {
  const [readers, setReaders] = useState(null);
  const [releases, setReleases] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // the reader being edited, and the ticks
  const [userId, setUserId] = useState(null);
  const [have, setHave] = useState({ releases: [], groups: [] });
  const [tickReleases, setTickReleases] = useState(new Set());
  const [tickGroups, setTickGroups] = useState(new Set());
  const [saving, setSaving] = useState(false);
  /**
   * ⭐ ON BY DEFAULT, AND THAT IS THE FIX. The operator granted a collection to an existing
   * reader and went looking for the email: *"I did not receive the email and Resend doesn’t show
   * it."* Nothing was broken — only the invitation path mailed — but a grant nobody is told about
   * is a book nobody knows they have. ⓘ Still a checkbox, because granting to your own test
   * account should not send you mail.
   */
  const [notify, setNotify] = useState(true);

  // inviting somebody new
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLabel, setInviteLabel] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const [toRevoke, setToRevoke] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r1, r2, r3] = await Promise.all([
        apiClient.get('/recipients'),
        apiClient.get('/reader-access/releases'),
        apiClient.get('/invitations'),
      ]);
      setReaders(r1.data.recipients || []);
      setReleases(r2.data.releases || []);
      setInvitations(r3.data.invitations || []);
    } catch (e) {
      setReaders([]);
      setError('Could not load readers, releases or invitations.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Load one reader's current access, and seed the ticks from it. */
  const pickReader = async (id) => {
    setUserId(id);
    setNotice(null);
    if (!id) { setHave({ releases: [], groups: [] }); setTickReleases(new Set()); setTickGroups(new Set()); return; }
    try {
      const { data } = await apiClient.get(`/reader-access/${id}`);
      const access = data.access || { releases: [], groups: [] };
      setHave(access);
      setTickReleases(new Set(access.releases.map((r) => r.uuid)));
      setTickGroups(new Set(access.groups.map((g) => g.uuid)));
    } catch (e) {
      setError('Could not load what that reader has.');
    }
  };

  const tree = useMemo(() => groupReleases(releases), [releases]);
  const hadReleases = useMemo(() => new Set(have.releases.map((r) => r.uuid)), [have]);
  const hadGroups = useMemo(() => new Set(have.groups.map((g) => g.uuid)), [have]);

  /** ⭐ The diff, computed here and sent as the whole instruction. */
  const diff = useMemo(() => ({
    grantReleases: [...tickReleases].filter((u) => !hadReleases.has(u)),
    revokeReleases: [...hadReleases].filter((u) => !tickReleases.has(u)),
    grantGroups: [...tickGroups].filter((u) => !hadGroups.has(u)),
    revokeGroups: [...hadGroups].filter((u) => !tickGroups.has(u)),
  }), [tickReleases, tickGroups, hadReleases, hadGroups]);

  const changes = diff.grantReleases.length + diff.revokeReleases.length
    + diff.grantGroups.length + diff.revokeGroups.length;

  const toggle = (set, setter, uuid) => {
    const next = new Set(set);
    if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
    setter(next);
  };

  const save = async () => {
    if (!userId || !changes) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const { data } = await apiClient.post(`/reader-access/${userId}`, { ...diff, notify });
      const access = data.access || { releases: [], groups: [] };
      setHave(access);
      setTickReleases(new Set(access.releases.map((r) => r.uuid)));
      setTickGroups(new Set(access.groups.map((g) => g.uuid)));
      const bits = [];
      if (data.granted) bits.push(`${data.granted} granted`);
      if (data.revoked) bits.push(`${data.revoked} revoked`);
      if (data.groupsGranted) bits.push(`${data.groupsGranted} collection(s) granted`);
      if (data.groupsRevoked) bits.push(`${data.groupsRevoked} collection(s) revoked`);
      /**
       * ⓘ The send outcome is part of the result, not a separate request. A notification that
       * failed has to be visible here — otherwise the operator is back to checking Resend's
       * dashboard to find out whether their reader was told.
       */
      if (data.notified && data.notified.sent) bits.push(`emailed ${data.notified.to}`);
      else if (notify && (data.granted || data.groupsGranted)) {
        bits.push(`NOT emailed${data.notified && data.notified.error ? ` (${data.notified.error})` : ''}`);
      }
      setNotice(bits.join(', ') || 'Nothing changed.');
      // ⓘ A partial failure is reported rather than swallowed: the server applies what it can and
      // says which items did not take.
      if (data.failed && data.failed.length) {
        setError(`${data.failed.length} change(s) did not apply: ${data.failed.map((f) => f.error).join('; ')}`);
      }
      await load();
    } catch (e) {
      setError('Could not save those changes.');
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim() || !(tickReleases.size || tickGroups.size)) return;
    setInviting(true); setError(null); setNotice(null); setLastInvite(null);
    try {
      const { data } = await apiClient.post('/invitations', {
        email: inviteEmail.trim(),
        name: inviteName.trim() || null,
        label: inviteLabel.trim() || null,
        releaseUuids: [...tickReleases],
        litGroupUuids: [...tickGroups],
        send: true,
      });
      setLastInvite(data);
      setNotice(data.delivery && data.delivery.sent
        ? `Invitation emailed to ${inviteEmail.trim()}.`
        : 'Invitation created, but the email did not send — the link is below.');
      setInviteEmail(''); setInviteName(''); setInviteLabel('');
      await load();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || 'Could not create that invitation.');
    } finally {
      setInviting(false);
    }
  };

  const reissue = async (inv) => {
    setError(null); setNotice(null);
    try {
      const { data } = await apiClient.post(`/invitations/${inv.uuid}/reissue`, {});
      setLastInvite(data);
      setNotice(data.delivery && data.delivery.sent
        ? `A new invitation was emailed to ${inv.email}.`
        : 'A new invitation was created, but the email did not send — the link is below.');
      await load();
    } catch (e) {
      setError('Could not reissue that invitation.');
    }
  };

  const doRevoke = async () => {
    setRevoking(true);
    try {
      await apiClient.post(`/invitations/${toRevoke.uuid}/revoke`, {});
      setToRevoke(null);
      setNotice('Invitation revoked. That link no longer works.');
      await load();
    } catch (e) {
      setError('Could not revoke that invitation.');
    } finally {
      setRevoking(false);
    }
  };

  const readerOptions = useMemo(() => (readers || []).map((r) => ({
    key: r.userId, value: r.userId, text: readerLabel(r),
  })), [readers]);

  /** The state of an invitation, as one word. */
  const inviteState = (inv) => {
    if (inv.revokedAt) return { text: 'revoked', color: 'grey' };
    if (inv.usedAt) return { text: 'claimed', color: 'green' };
    if (inv.expiresAt && new Date(inv.expiresAt) < new Date()) return { text: 'expired', color: 'orange' };
    if (inv.lastSendError) return { text: 'send failed', color: 'red' };
    if (inv.sentAt) return { text: 'sent', color: 'blue' };
    return { text: 'not sent', color: 'yellow' };
  };

  return (
    <Segment basic style={{ padding: '1rem 1.5rem' }}>
      <Header as="h2">
        Reader access
        <Header.Subheader>
          Tick what someone may listen to. An invitation emails a one-time link; changing an
          existing reader grants it straight away, and tells them if you ask it to.
        </Header.Subheader>
      </Header>

      {error && <Message negative content={error} onDismiss={() => setError(null)} />}
      {notice && <Message positive content={notice} onDismiss={() => setNotice(null)} />}

      {readers === null && <Loader active inline="centered" content="Loading…" />}

      {readers !== null && (
        <>
          <Segment>
            <Header as="h4">1. What may they hear?</Header>
            {/*
              ⚠️ A COLLECTION TICK IS NOT A SHORTHAND FOR ITS BOOKS. It is a standing grant: later
              books appear on the shelf by themselves. Said here because the two controls look
              alike and mean different things.
            */}
            {!tree.length && <Message info content="No ready releases to give yet." />}
            {tree.map((g) => (
              <Segment key={g.uuid} vertical>
                <Checkbox
                  checked={tickGroups.has(g.uuid)}
                  onChange={() => toggle(tickGroups, setTickGroups, g.uuid)}
                  label={(
                    <label>
                      <strong>{g.name}</strong>
                      {' '}
                      <span style={{ color: 'rgba(0,0,0,0.5)' }}>
                        — the whole collection...
                      </span>
                      {hadGroups.has(g.uuid) && <Label size="mini" color="green" style={{ marginLeft: '0.5rem' }}>has</Label>}
                    </label>
                  )}
                />
                <div style={{ margin: '0.5rem 0 0 1.75rem' }}>
                  {g.books.map((b) => (
                    <div key={b.uuid} style={{ margin: '0 0 0.35rem' }}>
                      <div style={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.9em' }}>{b.title}</div>
                      {b.releases.map((r) => (
                        <Checkbox
                          key={r.uuid}
                          style={{ display: 'inline-table', marginRight: '1.25rem' }}
                          checked={tickReleases.has(r.uuid)}
                          onChange={() => toggle(tickReleases, setTickReleases, r.uuid)}
                          label={(
                            <label>
                              {r.title}
                              {' '}
                              <span style={{ color: 'rgba(0,0,0,0.45)' }}>
                                ({r.kind === 'book' ? 'whole book' : 'chapters'}, {fmtDate(r.createdAt)})
                              </span>
                              {hadReleases.has(r.uuid) && <Label size="mini" color="green" style={{ marginLeft: '0.4rem' }}>has</Label>}
                            </label>
                          )}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </Segment>
            ))}
          </Segment>

          <Segment>
            <Header as="h4">2a. Change an existing reader</Header>
            {/*
              ⭐⭐ SEMANTIC CHECKBOX LAYOUT, THE DOCUMENTED TRAP — see ClientReleasesManager’s header.
              A Semantic `Checkbox` draws its box with `position: absolute` and its text in a
              `<label>` that is NOT counted in the element’s width, so inside a `widths="equal"`
              group the label runs straight over whatever comes next. The operator saw it draw over
              the Save button AND the segment’s rule below it.

              ⛔ `display: inline-table` is the repo’s fix and is NOT sufficient on its own here: a
              table box shrink-wraps to its content, so a long label in an equal-width field simply
              overflows the field instead. The label needs a row of its own, with `maxWidth` so the
              text WRAPS rather than reaching for more width.
            */}
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <Dropdown
                    selection search clearable
                    placeholder="Pick a reader"
                    options={readerOptions}
                    value={userId}
                    onChange={(e, d) => pickReader(d.value || null)}
                  />
                </Form.Field>
                <Form.Field>
                  <Button primary disabled={!userId || !changes || saving} loading={saving} onClick={save}>
                    {changes ? `Save ${changes} change(s)` : 'No changes'}
                  </Button>
                </Form.Field>
              </Form.Group>
              <Form.Field style={{ margin: 0 }}>
                <Checkbox
                  style={{ display: 'inline-table', maxWidth: '100%' }}
                  checked={notify}
                  onChange={() => setNotify(!notify)}
                  label="Email them that their shelf grew"
                />
                <div style={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.9em', margin: '0.2rem 0 0 1.75rem' }}>
                  Only when something is added — a revoke sends nothing.
                </div>
              </Form.Field>
            </Form>
            {!!userId && (
              <p style={{ color: 'rgba(0,0,0,0.6)' }}>
                Has {have.releases.length} book(s) and {have.groups.length} collection(s).
                {' '}Ticking a box grants; clearing one revokes.
              </p>
            )}
          </Segment>

          <Segment>
            <Header as="h4">2b. Or invite somebody new</Header>
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <Input
                    placeholder="their@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </Form.Field>
                <Form.Field>
                  <Input
                    placeholder="Their name (optional)"
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                  />
                </Form.Field>
                <Form.Field>
                  <Input
                    placeholder="Your note, e.g. QC reader (optional)"
                    value={inviteLabel}
                    onChange={(e) => setInviteLabel(e.target.value)}
                  />
                </Form.Field>
                <Form.Field>
                  <Button
                    primary
                    disabled={!inviteEmail.trim() || !(tickReleases.size || tickGroups.size) || inviting}
                    loading={inviting}
                    onClick={sendInvite}
                  >
                    Send invitation
                  </Button>
                </Form.Field>
              </Form.Group>
            </Form>
            <p style={{ color: 'rgba(0,0,0,0.6)' }}>
              {/* ⛔ The honest description of what gets mailed, because it is NOT a key. */}
              They get a one-time link that expires in 14 days. No access key is emailed — the link
              grants nothing until they claim it, and it can be revoked afterwards.
            </p>

            {/*
              ⭐ THE LINK IS SHOWN ONCE, like a key. Nothing stores it, so if the mail bounces this
              is the only chance to deliver it by hand.
            */}
            {lastInvite && lastInvite.inviteUrl && (
              <Message info>
                <Message.Header>The link, shown once</Message.Header>
                <p style={{ wordBreak: 'break-all' }}><code>{lastInvite.inviteUrl}</code></p>
                <Button
                  size="tiny" basic icon labelPosition="left"
                  onClick={() => navigator.clipboard.writeText(lastInvite.inviteUrl).catch(() => { })}
                >
                  <Icon name="copy" />Copy
                </Button>
              </Message>
            )}
          </Segment>

          <Segment>
            <Header as="h4">
              Invitations
              {/*
                ⛔ NO `icon labelPosition="left"`, AND THAT IS WHAT MAKES IT SMALL. Semantic reserves a
                fat icon gutter for that combination — 2.5em of left padding with the glyph absolutely
                positioned inside it — so `compact size="tiny"` could not shrink the button past the
                gutter. Dropping it makes the icon an ordinary inline glyph that scales with the type.

                ⓘ Sized to match the /listen controls: ~0.78rem of type, 6px/10px of padding.
              */}
              <Button
                basic compact size="mini" floated="right" onClick={load}
                title="Re-read the invitations from the server"
                style={{ fontSize: '0.78rem', padding: '6px 10px', margin: 0 }}
              >
                <Icon name="refresh" style={{ marginRight: '0.4em', opacity: 0.8 }} />
                Refresh
              </Button>
            </Header>
            {!invitations.length && <Message info content="No invitations yet." />}
            {!!invitations.length && (
              <Table compact celled>
                <Table.Header>
                  <Table.Row>
                    {/**
                      * ⓘ THE WIDTH LIVES ON THE HEADER CELL. An auto-layout table sizes a
                      * column from its widest content and treats a width on a body cell as a
                      * suggestion it may ignore; the header is where the browser takes it as the
                      * column preference. The wrapping is set on the body cell below, because a
                      * preference without it just makes one very wide row.
                      */}
                    <Table.HeaderCell style={{ width: '500px' }}>To</Table.HeaderCell>
                    <Table.HeaderCell>For</Table.HeaderCell>
                    <Table.HeaderCell>State</Table.HeaderCell>
                    <Table.HeaderCell>Expires</Table.HeaderCell>
                    <Table.HeaderCell />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {invitations.map((inv) => {
                    const st = inviteState(inv);
                    return (
                      <Table.Row key={inv.uuid} verticalAlign="top">
                        {/**
                          * ⛔ overflowWrap: anywhere, not break-word. A long address or label is
                          * one unbroken token with no space to break at, so break-word leaves it
                          * sticking out and stretches the column past the 500px asked for.
                          * maxWidth on the inner div binds whatever the table decides to do with
                          * the cell.
                          */}
                        <Table.Cell style={{ maxWidth: '500px' }}>
                          <div style={{ maxWidth: '500px', overflowWrap: 'anywhere' }}>
                            {inv.email || <em>any address</em>}
                            {inv.label && <div style={{ color: 'rgba(0,0,0,0.5)' }}>{inv.label}</div>}
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          {(inv.titles || []).join(', ')}
                          {!!(inv.groups || []).length && (
                            <div><strong>{inv.groups.join(', ')}</strong> (whole collection)</div>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <Label size="mini" color={st.color}>{st.text}</Label>
                          {inv.lastSendError && (
                            <div style={{ color: '#9f3a38', fontSize: '0.85em' }}>{inv.lastSendError}</div>
                          )}
                        </Table.Cell>
                        <Table.Cell>{fmtDate(inv.expiresAt)}</Table.Cell>
                        <Table.Cell>
                          {!inv.usedAt && !inv.revokedAt && (
                            <>
                              {/* ⓘ Reissue, not resend: the token cannot be recovered, so this makes
                                  a new one and revokes this. */}
                              <Button size="tiny" basic onClick={() => reissue(inv)}>Send again</Button>
                              <Button size="tiny" basic negative onClick={() => setToRevoke(inv)}>Revoke</Button>
                            </>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            )}
          </Segment>
        </>
      )}

      <ConfirmModal
        open={!!toRevoke}
        title="Revoke this invitation?"
        items={toRevoke ? [toRevoke.email || 'any address'] : []}
        note="The link stops working at once. Nothing they already claimed is affected — this only stops an unclaimed invitation."
        confirmLabel="Revoke"
        loading={revoking}
        onConfirm={doRevoke}
        onCancel={() => setToRevoke(null)}
      />
    </Segment>
  );
}
